import { ACCOUNT_RESOURCE_TYPE, ENTITLEMENT_SUBACCOUNTS } from '@headline/schemas'
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { Link, createFileRoute } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { AccountAccessSection } from '@/components/accounts/AccountAccessSection'
import { DashboardVettingSection } from '@/components/accounts/DashboardVettingSection'
import { DeleteAccountSection } from '@/components/accounts/DeleteAccountSection'
import { InheritTopicsSection } from '@/components/accounts/InheritTopicsSection'
import { ProductsSection } from '@/components/accounts/ProductsSection'
import { SeatsSection } from '@/components/accounts/SeatsSection'
import { SubaccountsSection } from '@/components/accounts/SubaccountsSection'
import { PageHeader } from '@/components/PageHeader'
import { useCanEditAccount } from '@/lib/use-account-permissions'
import { isStaff } from '@/lib/viewer'
import {
  accessOverviewQuery,
  accountQuery,
  membersQuery,
  myResourcePermissionsQuery,
  sessionQuery,
  subaccountsQuery,
} from '@/lib/queries'

/**
 * Sub-accounts & settings for one account — reachable by anyone with access to the account, with the
 * internal-only parts gated PER SECTION rather than the whole page walled off. A customer's own admin
 * comes here to add people within the account's seat allowance and to toggle topic inheritance; the
 * provisioning acts — creating/renaming/deleting sub-accounts, products, dashboard vetting, the seat
 * CAP itself, and deletion — show only for Clipbook staff, and their APIs enforce `manage-all:accounts`
 * regardless of what the UI renders.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/settings')({
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(accountQuery(params.accountId)),
      context.queryClient.ensureQueryData(subaccountsQuery(params.accountId)),
    ]),
  component: AccountSettingsPage,
})

function AccountSettingsPage() {
  const { accountId } = Route.useParams()
  const queryClient = useQueryClient()
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data: subaccounts } = useSuspenseQuery(subaccountsQuery(accountId))
  const { data: session } = useSuspenseQuery(sessionQuery)
  const isClipbookAdmin = isStaff(session.user)
  // May this user change things here — the UI mirror of account:edit (staff bypass included).
  const canEdit = useCanEditAccount(accountId)

  // Two ways to be allowed to manage access here, and only one is in the session. Managing every
  // account's access is Clipbook's, decided by the API (`managesAllAccess`, CLI-926 — the slug alone
  // can't tell a customer's admin from Clipbook's); a customer's own admin holds
  // `account:manage-access` ON this account, which the JWT knows nothing about — so the screen asks.
  const orgWideAccessManager = session.user?.managesAllAccess ?? false
  const myPermissions = useQuery({
    ...myResourcePermissionsQuery(ACCOUNT_RESOURCE_TYPE, accountId),
    enabled: !orgWideAccessManager,
    retry: false,
  })
  const canManageAccess =
    orgWideAccessManager ||
    (myPermissions.data?.permissions.includes('account:manage-access') ?? false)

  // Admin-gated, fetched only when the session can actually reach it — `enabled` keeps a viewer from
  // firing a request that can only 403.
  const access = useQuery({
    ...accessOverviewQuery(accountId),
    enabled: canManageAccess,
    retry: false,
  })
  // Autocomplete for the picker. Listing an organization's members is an org-wide read behind
  // `org-users:manage`, which a delegated access manager doesn't have — so it's fetched only for
  // internal admins, and the picker falls back to plain typing for everyone else.
  const members = useQuery({ ...membersQuery, enabled: orgWideAccessManager })

  return (
    <div className="flex flex-col gap-8">
      <Link
        to="/app/accounts/$accountId"
        params={{ accountId }}
        className="flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-3.5" />
        {account.name}
      </Link>
      <PageHeader
        title="Sub-accounts & settings"
        description={
          // A count of 1 is the hidden default — saying "1 sub-account" leaks the level the UI keeps
          // quiet until it distinguishes something.
          account.subaccountCount > 1
            ? `${account.subaccountCount} sub-accounts · ${account.status}`
            : account.status
        }
      />

      <SubaccountsSection
        accountId={accountId}
        subaccounts={subaccounts.subaccounts}
        // The entitlement still governs whether the account *could* be split, but only staff act on
        // it now — the customer sees the list read-only.
        canAddSubaccounts={isClipbookAdmin}
        // Structure — add / rename / delete — is a provisioning act reserved to staff; the API gates
        // it on manage-all:accounts, so this hides controls that would only 403 for a customer. The
        // list itself stays visible to anyone with account access.
        canEdit={isClipbookAdmin}
        byAdminBypass={isClipbookAdmin && !account.entitlements.includes(ENTITLEMENT_SUBACCOUNTS)}
        queryClient={queryClient}
      />

      {/* The account's own topic roll-up preference — set by its admin (account:edit) or staff, so
          not gated to internal here; the toggle disables itself for a plain viewer. */}
      <InheritTopicsSection account={account} canEdit={canEdit} queryClient={queryClient} />

      {/* Absent for anyone but Clipbook staff. What a customer bought and the seat cap their admins
          grant within are Clipbook's to set; dashboard vetting is Clipbook's own record, which a
          client never sees and whose routes refuse them. */}
      {isClipbookAdmin && (
        <>
          <ProductsSection
            accountId={accountId}
            entitlements={account.entitlements}
            queryClient={queryClient}
          />
          <DashboardVettingSection accountId={accountId} queryClient={queryClient} />
          <SeatsSection
            account={account}
            subaccounts={subaccounts.subaccounts}
            queryClient={queryClient}
          />
        </>
      )}

      {/* Absent, not disabled, for someone who can't manage access — the section would be a row of
          controls that only ever produce 403s. */}
      {canManageAccess && (
        <AccountAccessSection
          accountId={accountId}
          registered={account.resourceRegistered}
          overview={access.data}
          memberEmails={
            members.data?.members.flatMap((member) => (member.email ? [member.email] : [])) ?? []
          }
          error={access.error instanceof Error ? access.error.message : null}
          queryClient={queryClient}
        />
      )}

      {/* Same gate as creating an account: removing a customer and everyone's access to it is an
          internal act, not something the per-account edit permission should carry. */}
      {isClipbookAdmin && (
        <DeleteAccountSection
          accountId={accountId}
          name={account.name}
          subaccountCount={account.subaccountCount}
          queryClient={queryClient}
        />
      )}
    </div>
  )
}
